import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';

interface SortStrategyBucket {
  id?: number;
  sortStrategyId?: number;
  name: string;
  description: string;
  bucketOrder: number;
  queryFilters?: Record<string, any>;
  queryExclusions?: Record<string, any>;
  advancedFilters?: {
    groups: Array<{
      operator: 'AND' | 'OR';
      filters: Record<string, any>;
    }>;
    groupOperator: 'AND' | 'OR';
  };
  sortBy: string;
  pullLimit?: number | null;
}

interface SortStrategyData {
  id?: number;
  name: string;
  description: string;
  repeat: boolean;
  isActive: boolean;
  buckets: SortStrategyBucket[];
}

class SortStrategyApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/catalog/sort-strategy');
  }

  async updateById<T = any>(
    id: string | number,
    data: Partial<T>,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    try {
      const response = await axiosInstance.put(`${this.baseURL}/${id}`, data, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating sort strategy:', error);
      throw error;
    }
  }

  async deleteById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.delete(`${this.baseURL}/${id}`, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting sort strategy:', error);
      throw error;
    }
  }

  async create<T = any>(payload: Partial<T>, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/1`, payload, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating sort strategy:', error);
      throw error;
    }
  }

  // Search sort strategies
  async search(payload: any): Promise<any> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/search`, payload, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching sort strategies:', error);
      throw error;
    }
  }

  // Get collections for a sort strategy
  async getCollections(strategyId: string | number): Promise<number[]> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/${strategyId}/collections`, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
        },
      });
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching sort strategy collections:', error);
      throw error;
    }
  }

  // Add collection to sort strategy
  async addCollection(strategyId: string | number, collectionId: string | number): Promise<any> {
    try {
      const response = await axiosInstance.post(
        `${this.baseURL}/${strategyId}/collections/${collectionId}`,
        {},
        {
          headers: {
            'x-lk-orgid': 'lyskraft',
            'x-lk-portalcode': 'pos',
            'x-lk-scid': '1',
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding collection to sort strategy:', error);
      throw error;
    }
  }

  // Remove collection from sort strategy
  async removeCollection(strategyId: string | number, collectionId: string | number): Promise<any> {
    try {
      const response = await axiosInstance.delete(
        `${this.baseURL}/${strategyId}/collections/${collectionId}`,
        {
          headers: {
            'x-lk-orgid': 'lyskraft',
            'x-lk-portalcode': 'pos',
            'x-lk-scid': '1',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing collection from sort strategy:', error);
      throw error;
    }
  }

  // Method to get facets for filtering (reuse from collections)
  async getFacets(): Promise<any> {
    try {
      const response = await axiosInstance.post(
        GATEWAY_API_URL + '/search/styles',
        {
          searchType: 'FREETEXT',
          text: '',
          selectedFacets: {},
          page: 1,
          size: 1,
        },
        {
          headers: {
            'x-lk-orgid': 'lyskraft',
            'x-lk-portalcode': 'pos',
            'x-lk-scid': '1',
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data.data.facets || [];
    } catch (error) {
      console.error('Error fetching facets:', error);
      throw error;
    }
  }
}

export default SortStrategyApiClient;